import { useEffect, useState } from "react";
import {
  ChevronLeft, ChevronRight, Loader2, Pencil, Plus, Save, Search,
  Tag as TagIcon, Trash2, X,
} from "lucide-react";

import { useConfirm } from "@/components/confirm-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useCreateTag,
  useDeleteTag,
  useTags,
  useUpdateTag,
} from "@/hooks/useTags";
import type { Tag } from "@/lib/types";
import { cn } from "@/lib/utils";

const PAGE_SIZE = 20;

/**
 * /tags — Director+ surface. Inline new-tag input at the top, alphabetical
 * grid of pill rows below, per-row Edit / Delete with a small modal.
 *
 * Tags are global (not per-document), so editing one renames it everywhere
 * it's attached, and deleting one removes the M2M link from every Document
 * that had it. The API just hard-deletes the row — there's no usage-count
 * gate yet, so the confirm copy is explicit about the side-effect.
 */
export default function Tags() {
  const [search,      setSearch]      = useState("");
  const [searchDraft, setSearchDraft] = useState("");
  const [page,        setPage]        = useState(1);
  const [newName,     setNewName]     = useState("");
  const [editing,     setEditing]     = useState<Tag | null>(null);

  const list    = useTags({ search: search || undefined, page });
  const create  = useCreateTag();
  const del     = useDeleteTag();
  const confirm = useConfirm();

  const totalPages = list.data ? Math.max(1, Math.ceil(list.data.count / PAGE_SIZE)) : 1;

  function onSearchSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSearch(searchDraft.trim()); setPage(1);
  }

  function onCreate(e: React.FormEvent) {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    create.mutate(name, { onSuccess: () => setNewName("") });
  }

  async function onDelete(t: Tag) {
    const ok = await confirm({
      title: `Delete tag "${t.name}"?`,
      description: "It will be removed from every document that uses it.",
      confirmLabel: "Delete tag",
      destructive: true,
    });
    if (!ok) return;
    del.mutate(t.id);
  }

  const createError = (create.error as
    | { response?: { data?: { name?: string[]; detail?: string } } } | undefined)
    ?.response?.data;

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      {/* ---------- Header ---------- */}
      <header className="flex items-start gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-emerald-700 text-primary-foreground shadow-soft">
          <TagIcon className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Tags</h1>
          <p className="text-sm text-muted-foreground">
            Global labels attached to documents. Used in the upload form's tag picker.
          </p>
        </div>
      </header>

      {/* ---------- Create + search bar ---------- */}
      <Card className="border-border/60 shadow-soft">
        <CardContent className="flex flex-col gap-3 p-4 md:flex-row md:items-center">
          <form onSubmit={onCreate} className="flex flex-1 items-center gap-2">
            <Input
              placeholder="New tag name…"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              maxLength={50}
              className="h-10"
            />
            <Button type="submit" disabled={!newName.trim() || create.isPending}>
              {create.isPending
                ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Adding…</>
                : <><Plus className="mr-2 h-4 w-4" />Add tag</>}
            </Button>
          </form>

          <form onSubmit={onSearchSubmit} className="relative md:w-72">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search…"
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
              className="h-10 pl-9"
            />
          </form>
        </CardContent>
      </Card>

      {createError?.name?.[0] && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {createError.name[0]}
        </div>
      )}

      {/* ---------- Tag grid ---------- */}
      <Card className="border-border/60 shadow-soft">
        <CardContent className="p-4">
          {list.isLoading ? (
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 9 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : list.data && list.data.results.length > 0 ? (
            <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {list.data.results.map((t) => (
                <li
                  key={t.id}
                  className="group flex items-center justify-between gap-2 rounded-md border border-border/60 bg-card px-3 py-2 transition-colors hover:bg-accent/30"
                >
                  <Badge variant="secondary" className="font-medium">
                    <TagIcon className="mr-1.5 h-3 w-3" />
                    {t.name}
                  </Badge>
                  <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                    <Button
                      variant="ghost" size="icon" className="h-7 w-7"
                      onClick={() => setEditing(t)}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                      <span className="sr-only">Edit {t.name}</span>
                    </Button>
                    <Button
                      variant="ghost" size="icon"
                      className="h-7 w-7 text-destructive hover:text-destructive"
                      disabled={del.isPending}
                      onClick={() => onDelete(t)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      <span className="sr-only">Delete {t.name}</span>
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="px-6 py-16 text-center">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                <TagIcon className="h-5 w-5 text-muted-foreground" />
              </div>
              <h3 className="text-sm font-semibold">
                {search ? "No tags match" : "No tags yet"}
              </h3>
              <p className="mt-1 text-xs text-muted-foreground">
                {search ? "Try a different search." : "Add the first one above."}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ---------- Pagination ---------- */}
      {list.data && list.data.count > PAGE_SIZE && (
        <div className="flex items-center justify-between gap-4">
          <div className="text-sm text-muted-foreground">
            <span className="font-mono font-semibold tabular-nums">
              {list.data.count.toLocaleString()}
            </span>{" "}
            tag{list.data.count === 1 ? "" : "s"}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline" size="sm"
              disabled={!list.data.previous}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <ChevronLeft className="h-4 w-4" />Previous
            </Button>
            <div className="rounded-md border bg-card px-3 py-1.5 text-sm font-mono tabular-nums text-muted-foreground">
              {page} / {totalPages}
            </div>
            <Button
              variant="outline" size="sm"
              disabled={!list.data.next}
              onClick={() => setPage((p) => p + 1)}
            >
              Next<ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <TagEditDialog
        tag={editing}
        onClose={() => setEditing(null)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Edit dialog — kept inline since it's tiny (one field).
// ---------------------------------------------------------------------------

function TagEditDialog({
  tag, onClose,
}: {
  tag: Tag | null;
  onClose: () => void;
}) {
  const update = useUpdateTag();
  const [name, setName] = useState("");

  useEffect(() => {
    if (tag) setName(tag.name);
    update.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tag?.id]);

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!tag) return;
    const next = name.trim();
    if (!next || next === tag.name) { onClose(); return; }
    update.mutate({ id: tag.id, name: next }, { onSuccess: onClose });
  }

  const err = (update.error as
    | { response?: { data?: { name?: string[]; detail?: string } } } | undefined)
    ?.response?.data;
  const errorMsg = err?.detail ?? err?.name?.[0];

  return (
    <Dialog open={tag !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Rename tag</DialogTitle>
          <DialogDescription>
            The new name appears on every document that uses this tag.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="tag-name">Name</Label>
            <Input
              id="tag-name" required maxLength={50}
              value={name} onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </div>

          {errorMsg && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {errorMsg}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose} disabled={update.isPending}>
              <X className="mr-2 h-4 w-4" />Cancel
            </Button>
            <Button
              type="submit"
              disabled={!name.trim() || name.trim() === tag?.name || update.isPending}
              className={cn(name.trim() === tag?.name && "opacity-50")}
            >
              {update.isPending
                ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving…</>
                : <><Save className="mr-2 h-4 w-4" />Save</>}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
